
import React, { useState, useEffect } from 'react';
import { useProviderWizard } from '@/hooks/admin/provider-wizard/useProviderWizard';
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from 'react-router-dom';
import { WizardProgress } from './WizardProgress';
import { WizardNav } from './WizardNav';
import { WizardStepRenderer } from './WizardStepRenderer';
import { WizardActions } from './WizardActions';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Create a client
const queryClient = new QueryClient();

export const ProviderWizard = () => {
  const {
    currentStep,
    formData,
    isLastStep,
    handleNext,
    handleBack,
    handleStepSubmit,
    isSubmitting,
    isStepValid,
    totalSteps
  } = useProviderWizard();

  const { toast } = useToast();
  const navigate = useNavigate();
  const [isFormValid, setIsFormValid] = useState(true); // Default to true to allow starting the wizard

  const stepLabels = [
    "Provider Details",
    "User Assignment",
    "Lead Account",
    "Strategy Setup",
    "Account Setup",
    "Review"
  ];

  // Track form validity for the active step
  useEffect(() => {
    // Default to valid for the first step to get started
    if (currentStep === 0) {
      setIsFormValid(true);
    } else {
      setIsFormValid(isStepValid(currentStep));
    }
  }, [currentStep, isStepValid]);

  const handleFinalSubmit = async () => {
    try {
      await handleNext();
      toast({
        title: "Success!",
        description: "Provider has been successfully created",
      });
      navigate('/admin');
    } catch (error) {
      console.error("Error creating provider:", error);
      toast({
        title: "Error",
        description: "There was a problem creating the provider",
        variant: "destructive",
      });
    }
  };

  const handleStepNext = () => {
    // For user assignment (step 1), always allow continuing
    if (currentStep === 1) {
      handleNext();
      return;
    }
    
    // Submit the current form
    const form = document.querySelector('form');
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      // If no form (like in summary step), just proceed
      handleNext();
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <div className="space-y-8">
        <WizardProgress currentStep={currentStep} totalSteps={totalSteps} stepLabels={stepLabels} />
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-3 lg:col-span-2">
            <WizardNav
              currentStep={currentStep}
              totalSteps={totalSteps}
              stepLabels={stepLabels}
              onStepClick={() => {}} // This would be implemented to jump to steps
              isStepCompleted={idx => idx < currentStep}
            />
          </div>
          
          <div className="md:col-span-9 lg:col-span-10">
            <WizardStepRenderer
              currentStep={currentStep}
              stepLabels={stepLabels}
              totalSteps={totalSteps}
              formData={formData}
              isSubmitting={isSubmitting}
              handleStepSubmit={handleStepSubmit}
              setIsFormValid={setIsFormValid}
              handleNext={handleNext}
            />

            <WizardActions
              currentStep={currentStep}
              isLastStep={isLastStep}
              isSubmitting={isSubmitting}
              isFormValid={isFormValid}
              onBack={handleBack}
              onNext={handleStepNext}
              onSubmit={handleFinalSubmit}
            />
          </div>
        </div>
      </div>
    </QueryClientProvider>
  );
};
